import { FC } from 'react';
import { HStack, VStack } from '@chakra-ui/react';
import { ColorName } from '@shinetools/sunshine';
import { IconName } from '@shinetools/sunshine-icons';

import { ReferralDisplayedStatus } from '__generated__/GQL';
import SunshineTag from 'components/_core/SunshineTag';
import Text from 'components/_core/Text';
import { Referral } from 'features/Referral/hooks/useReferral';
import { formatDate, P } from 'helpers/date';

import locales from '../locales';

interface PendingReferralsListItemProps {
  item: Referral;
}

type PendingReferralDisplayedStatus = Exclude<
  ReferralDisplayedStatus,
  | typeof ReferralDisplayedStatus.ReferrerRewarded
  | typeof ReferralDisplayedStatus.Sleeping
  | typeof ReferralDisplayedStatus.MaxReferrerRewardsReached
>;

const PendingReferralsListItem: FC<PendingReferralsListItemProps> = ({
  item,
}) => {
  const { displayedStatus, referreeUser } = item;

  if (!referreeUser) {
    return null;
  }

  const icons = {
    [ReferralDisplayedStatus.UserCreated]: 'clock',
    [ReferralDisplayedStatus.NoTransaction]: 'unlock',
    [ReferralDisplayedStatus.AccountOpened]: 'unlock',
    [ReferralDisplayedStatus.Sleeping]: 'cross',
  };

  const colors = {
    [ReferralDisplayedStatus.UserCreated]: 'grey',
    [ReferralDisplayedStatus.NoTransaction]: 'blue',
    [ReferralDisplayedStatus.AccountOpened]: 'blue',
    [ReferralDisplayedStatus.Sleeping]: 'grey',
  };

  const label =
    locales.items.labels[displayedStatus as PendingReferralDisplayedStatus];
  const labelIcon = icons[
    displayedStatus as PendingReferralDisplayedStatus
  ] as IconName;
  const labelColor = colors[
    displayedStatus as PendingReferralDisplayedStatus
  ] as ColorName;

  return (
    <HStack
      border="1px solid"
      borderColor="grey.300"
      borderRadius={8}
      justifyContent="space-between"
      marginBottom="space-16"
      padding="space-16"
    >
      <VStack alignItems="flex-start" gap="space-2">
        <Text fontWeight={500}>
          {referreeUser.firstName} {referreeUser.lastName}
        </Text>
        <Text color="grey.700" fontWeight={400}>
          {locales.formatString(
            locales.registeredOn,
            formatDate(new Date(item.createdAt), P),
          )}
        </Text>
        {displayedStatus === ReferralDisplayedStatus.NoTransaction ? (
          <Text color="grey.600" size="sm">
            {locales.shouldMakeATransaction}
          </Text>
        ) : null}
      </VStack>

      <SunshineTag
        alignSelf="flex-start"
        colorScheme={labelColor}
        icon={labelIcon}
        size="sm"
      >
        {label}
      </SunshineTag>
    </HStack>
  );
};

export default PendingReferralsListItem;
